<template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-lg-10 col-sm-12 bg-white shadow-sm rounded-lg border mb-5">
                <form class="m-3" @submit.prevent="submit">
                    <!-- title -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.title }} <span class="required-star"></span>
                        </label>
                        <div class="col-sm-9">
                            <input type="text" class="form-control" maxlength="150" v-model="title" />
                            <span class="text-danger" v-if="errors && errors.title">{{ errors.title[0] }}</span>
                            <small class="form-text text-muted">
                                {{ trans.title_150_characters }}
                            </small>
                        </div>
                    </div>
                    <!-- image -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.image }} <span class="required-star"></span>
                        </label>
                        <div class="col-sm-9">
                            <label for="imageFile" role="button">
                                <img :src="audioGuideData ? audioGuideData.image_url : '/img/no-image.jpg'"
                                    class="icon-size-100" id="preview-image">
                            </label>
                            <input type="file" class="form-control-file d-none" id="imageFile" accept=".jpeg, .png, .jpg"
                                @change="uploadImage" />
                            <p class="text-danger" v-if="errors && errors.image">{{ errors.image[0] }}</p>
                            <small class="form-text text-muted">
                                {{ trans.maximum_audio_guide_size }}
                            </small>
                        </div>
                    </div>
                    <!-- description -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.description }}
                        </label>
                        <div class="col-sm-9">
                            <textarea class="form-control" v-model="description" rows="10"></textarea>
                            <span class="text-danger" v-if="errors && errors.description">{{
                                errors.description[0]
                            }}</span>
                        </div>
                    </div>
                    <!-- upload audio -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            Upload Audio <span class="required-star"></span>
                        </label>
                        <div class="col-sm-9">
                            <div class="form-group row d-flex justify-content-center align-items-center mb-2"
                                v-for="(audio, index) in audios" :key="index">
                                <div class="col-sm-3">
                                    <select class="form-control" v-model="audio.language">
                                        <option 
                                            v-for="(lang, langCode) in trans.lang"
                                            :value="langCode"
                                            :key="langCode"
                                        >
                                            {{ lang }}
                                        </option>
                                    </select>
                                    <span class="text-danger" v-if="getFirstError('audios.' + index + '.language')">{{
                                        getFirstError('audios.' + index + '.language')
                                    }}</span>
                                </div>
                                <div class="col-sm-7">
                                    <div class="row d-flex justify-content-center align-items-center">
                                        <div class="col-sm-4">
                                            <label :for="'inputUpload' + index" class="btn btn-secondary mb-0">
                                                <span class="font-weight-lighter">Upload File</span>
                                            </label>
                                            <input type="file" :id="'inputUpload' + index" class="form-control-file"
                                                accept="audio/mp3" @change="uploadAudio($event, index)" hidden>
                                        </div>
                                        <div class="col-sm-8 mt-1">
                                            <audio controls class="preview-audio w-100">
                                                <source :src="audio.file && '/storage/audio/' + audio.file">
                                            </audio>
                                        </div>
                                    </div>
                                    <span class="text-danger" v-if="getFirstError('audios.' + index + '.file')">{{
                                        getFirstError('audios.' + index + '.file')
                                    }}</span>
                                </div>
                                <div class="col-sm-2">
                                    <a role="button" class="btn btn-outline-secondary" @click="addAudio()">
                                        <i aria-hidden="true" class="fa fa-plus"></i>
                                    </a>
                                    <a role="button" class="btn btn-outline-secondary" @click="deleteAudio(index)">
                                        <i aria-hidden="true" class="fa fa-trash"></i>
                                    </a>
                                </div>
                            </div>
                            <small class="form-text text-muted">
                                {{ trans.maximum_mp3_size }}
                            </small>
                        </div>
                    </div>
                    <!-- status -->
                    <div class="form-group row mt-3 mb-3">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.status }}
                        </label>
                        <div class="col-sm-9 mt-2">
                            <div class="custom-control custom-switch">
                                <input type="checkbox" class="custom-control-input" id="customSwitch1" v-model="status"
                                    true-value="1" false-value="0" />
                                <label class="custom-control-label text-muted" for="customSwitch1">{{
                                    trans.status_val[status] }}</label>
                            </div>
                            <span class="text-danger" v-if="errors && errors.status">{{
                                errors.status[0]
                            }}</span>
                        </div>
                    </div>
                </form>
                <button class="btn btn-primary w-25 center mb-4 mt-4 float-right submit-btn" :disabled="isDisabled">
                    {{ this.audioGuideData ? trans.btn.update : trans.btn.create }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        trans: {
            type: Object,
            required: true,
        },
        formAction: {
            type: String,
            required: true,
        },
        audioGuideData: {
            type: Object,
            default: function () {
                return null;
            }
        },
        audioGuideFiles: {
            type: Array,
            default: function () {
                return null;
            }
        },
    },
    data() {
        return {
            title: this.audioGuideData ? this.audioGuideData.title : '',
            image: null,
            description: this.audioGuideData ? this.audioGuideData.description : '',
            audios: this.audioGuideData && this.audioGuideFiles.length > 0 ? this.audioGuideFiles : [{ language: '', file: null }],
            status: this.audioGuideData ? (this.audioGuideData.status ? 1 : 0) : 0,
            errors: {},
            isDisabled: false,
            removeAudios: [],
        };
    },
    methods: {
        prepareFormData() {
            let formData = new FormData();
            formData.append('status', this.status);
            formData.append('title', this.title);
            formData.append('description', this.description);
            if (this.image instanceof File) {
                formData.append("image", this.image);
            }
            if (this.audioGuideData) {
                formData.append('_method', 'PUT');
                formData.append('remove_audio', JSON.stringify(this.removeAudios));
            }
            for (let i = 0; i < this.audios.length; i++) {
                const element = this.audios[i];
                formData.append('audios[' + i + '][language]', element.language);
                formData.append('audios[' + i + '][file]', element.file instanceof File ? element.file : '');
                if (element.id) {
                    formData.append('audios[' + i + '][id]', element.id);
                }
            }
            
            return formData;
        },
        submit() {
            this.isDisabled = true;
            var self = this;
            axios({
                method: "POST",
                url: this.formAction,
                data: this.prepareFormData(),
                headers: {
                    'content-type': 'multipart/form-data'
                }
            })
                .then((response) => {
                    if (response.data.redirectTo != undefined) {
                        window.location.replace(response.data.redirectTo);
                    }
                })
                .catch((error) => {
                    self.isDisabled = false;
                    if (error.response.status == 422) {
                        this.errors = {};
                        this.errors = error.response.data.errors;
                    }
                });
        },
        uploadImage(event) {
            if (event.target.files.length == 0) return;
            const file = event.target.files;
            if (file) {
                let previewImage = document.getElementById("preview-image");
                previewImage.src = URL.createObjectURL(file[0]);
                this.image = file[0];
            }
        },
        uploadAudio(event, index) {
            if (event.target.files.length == 0) return;
            let file = event.target.files;
            if (file) {
                this.audios[index]['file'] = file[0]
                setTimeout(() => {
                    let audio = document.querySelectorAll('.preview-audio');
                    audio[index].src = URL.createObjectURL(file[0]);
                    audio[index].load()
                }, 500)
            }
        },
        addAudio() {
            this.audios.push({
                language: '',
                file: null
            });
        },
        deleteAudio(index) {
            this.removeAudios.push(this.audios[index]);
            this.audios.splice(index, 1);
            let audio = document.querySelectorAll('.preview-audio');
            audio[index].src = '';
        },
        getFirstError(field) {
            if (this.errors.length == 0) {
                return;
            }
            for (const key in this.errors) {
                if (key == field) {
                    return this.errors[key][0];
                }
            }
            return;
        },
        clickSubmitBtn() {
            var self = this;
            let submitBtns = document.getElementsByClassName('submit-btn');
            if (submitBtns.length > 0) {
                for (let i = 0; i < submitBtns.length; i++) {
                    const btn = submitBtns[i];
                    btn.addEventListener('click', function () {
                        self.submit();
                        this.disabled = true;
                        let formLanguages = document.querySelectorAll(".tab-content form");
                        formLanguages.forEach(function (form) {
                            var formData = new FormData(form);
                            axios.post(form.action, formData, {
                                headers: {
                                    "Content-Type": "multipart/form-data",
                                },
                            })
                        });
                    });
                }
            }
        },
    },
    mounted() {
        this.clickSubmitBtn();
    },
};
</script>

<style></style>